/**
 * Reports Metrics Service
 * Calculates real metrics from lessons and tasks for the Reports page
 *
 * UPDATED: 2024 - Added No-Show KPI tracking + Split Cancelled (Past/Upcoming)
 */

import { Lesson } from '../src/types/lesson'
import { Task } from './taskService'

export interface ReportsMetrics {
  activeStudents: number
  lessonCompletionRate: number
  noShowRate: number
  noShowCount: number
  cancellationRate: number
  taskCompletionRate: number
  totalLessons: number
  completedLessons: number
  noShowLessons: number
  cancelledLessons: number // DEPRECATED: Use pastCancelledLessons instead
  pastCancelledLessons: number // NEW: Past cancelled count
  upcomingCancelledLessons: number // NEW: Upcoming cancelled count
  totalTasks: number
  completedTasks: number
}

/**
 * Calculate all reports metrics from lessons and tasks
 * @param lessons - Array of lessons in the selected date range
 * @param tasks - Array of tasks in the selected date range
 * @returns ReportsMetrics object with all calculated values
 */
export function calculateReportsMetrics(
  lessons: Lesson[],
  tasks: Task[],
): ReportsMetrics {
  console.log('[REPORTS_METRICS] Calculating metrics from:', {
    lessonsCount: lessons.length,
    tasksCount: tasks.length,
  })

  // Get current timestamp for past/future filtering
  const now = new Date().getTime()

  // ============================================================================
  // 1. ACTIVE STUDENTS
  // Definition: Students with at least one non-cancelled lesson in date range
  // ============================================================================

  const nonCancelledLessons = lessons.filter(
    (lesson) => !lesson.cancelled && lesson.status !== 'CANCELED',
  )

  const uniqueStudentIds = new Set(
    nonCancelledLessons
      .filter((lesson) => lesson.studentId)
      .map((lesson) => lesson.studentId),
  )

  const activeStudents = uniqueStudentIds.size

  console.log('[REPORTS_METRICS] Active students calculation:', {
    totalLessons: lessons.length,
    nonCancelledLessons: nonCancelledLessons.length,
    activeStudents,
  })

  // ============================================================================
  // 2. LESSON OUTCOMES (APPROVED DEFINITIONS)
  // ============================================================================

  // Filter to past lessons only (startAt < now)
  const pastLessons = lessons.filter((lesson) => {
    const lessonTime = new Date(lesson.startAt).getTime()
    return lessonTime < now
  })

  // Filter to future lessons (startAt >= now)
  const futureLessons = lessons.filter((lesson) => {
    const lessonTime = new Date(lesson.startAt).getTime()
    return lessonTime >= now
  })

  // Completed (Attended): status === "COMPLETED" && attended === true
  const completedLessons = pastLessons.filter(
    (lesson) =>
      lesson.status === 'COMPLETED' &&
      lesson.attended === true &&
      lesson.completedAt,
  ).length

  // No-Show: status === "COMPLETED" && attended === false
  const noShowLessons = pastLessons.filter(
    (lesson) =>
      lesson.status === 'COMPLETED' &&
      lesson.attended === false &&
      lesson.completedAt,
  ).length

  // Past Cancelled: status === "CANCELED" && startAt < now
  const pastCancelledLessons = pastLessons.filter(
    (lesson) => lesson.status === 'CANCELED',
  ).length

  // Upcoming Cancelled: status === "CANCELED" && startAt >= now
  const upcomingCancelledLessons = futureLessons.filter(
    (lesson) => lesson.status === 'CANCELED',
  ).length

  console.log('[REPORTS_METRICS] Lesson outcomes:', {
    pastLessons: pastLessons.length,
    futureLessons: futureLessons.length,
    completedLessons,
    noShowLessons,
    pastCancelledLessons,
    upcomingCancelledLessons,
  })

  // ============================================================================
  // 3. KPI CALCULATIONS (APPROVED FORMULAS)
  // ============================================================================

  // Completion Rate = Attended / (Attended + No-Show)
  // Cancelled lessons excluded from denominator
  const performanceDenominator = completedLessons + noShowLessons

  const lessonCompletionRate =
    performanceDenominator > 0
      ? Math.round((completedLessons / performanceDenominator) * 100)
      : 0

  // No-Show Rate = No-Show / (Attended + No-Show)
  // Cancelled lessons excluded from denominator
  const noShowRate =
    performanceDenominator > 0
      ? Math.round((noShowLessons / performanceDenominator) * 100)
      : 0

  // Cancellation Rate = Past Cancelled / Total Past Lessons (PAST-ONLY)
  const totalPastLessons = pastLessons.length
  const cancellationRate =
    totalPastLessons > 0
      ? Math.round((pastCancelledLessons / totalPastLessons) * 100)
      : 0

  console.log('[REPORTS_METRICS] KPI calculations:', {
    performanceDenominator,
    lessonCompletionRate,
    noShowRate,
    cancellationRate,
    pastCancelledLessons,
    upcomingCancelledLessons,
  })

  // ============================================================================
  // 4. TASK COMPLETION RATE
  // ============================================================================

  const completedTasks = tasks.filter(
    (task) => task.status === 'completed',
  ).length

  const totalTasks = tasks.length

  const taskCompletionRate =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

  console.log('[REPORTS_METRICS] Task completion calculation:', {
    completedTasks,
    totalTasks,
    taskCompletionRate,
  })

  return {
    activeStudents,
    lessonCompletionRate,
    noShowRate,
    noShowCount: noShowLessons,
    cancellationRate,
    taskCompletionRate,
    totalLessons: lessons.length,
    completedLessons,
    noShowLessons,
    cancelledLessons: pastCancelledLessons, // Backward compatibility
    pastCancelledLessons,
    upcomingCancelledLessons,
    totalTasks,
    completedTasks,
  }
}

/**
 * Filter lessons by date range
 * @param lessons - Array of all lessons
 * @param startDate - Start date (ISO string YYYY-MM-DD)
 * @param endDate - End date (ISO string YYYY-MM-DD)
 * @returns Filtered array of lessons within the date range
 */
export function filterLessonsByDateRange(
  lessons: Lesson[],
  startDate: string,
  endDate: string,
): Lesson[] {
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()

  return lessons.filter((lesson) => {
    const lessonDate = new Date(lesson.startAt).getTime()
    return lessonDate >= start && lessonDate <= end
  })
}

/**
 * Filter tasks by date range (using completedDate for completed, dueDate for incomplete)
 * @param tasks - Array of all tasks
 * @param startDate - Start date (ISO string YYYY-MM-DD)
 * @param endDate - End date (ISO string YYYY-MM-DD)
 * @returns Filtered array of tasks within the date range
 */
export function filterTasksByDateRange(
  tasks: Task[],
  startDate: string,
  endDate: string,
): Task[] {
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()

  // DIAGNOSTIC LOGGING - START
  console.log('═══════════════════════════════════════════════════════')
  console.log('[TASK FILTER DEBUG] filterTasksByDateRange called')
  console.log('[TASK FILTER DEBUG] Input tasks count:', tasks.length)
  console.log('[TASK FILTER DEBUG] Date range:', {
    startDate,
    endDate,
    startTimestamp: start,
    endTimestamp: end,
  })

  // Log completed tasks details
  const completedTasks = tasks.filter((t) => t.status === 'completed')
  console.log(
    '[TASK FILTER DEBUG] Completed tasks found:',
    completedTasks.length,
  )

  if (completedTasks.length > 0) {
    console.log('[TASK FILTER DEBUG] Sample completed task:', {
      id: completedTasks[0].id,
      status: completedTasks[0].status,
      dueDate: completedTasks[0].dueDate,
      completedDate: completedTasks[0].completedDate,
      updatedAt: completedTasks[0].updatedAt,
      dueDateTimestamp: completedTasks[0].dueDate
        ? new Date(completedTasks[0].dueDate).getTime()
        : 'N/A',
      completedDateTimestamp: completedTasks[0].completedDate
        ? new Date(completedTasks[0].completedDate).getTime()
        : 'N/A',
    })
  }
  // DIAGNOSTIC LOGGING - END

  const filtered = tasks.filter((task) => {
    // For completed tasks
    if (task.status === 'completed') {
      // Preferred: use completedDate (new behavior)
      if (task.completedDate) {
        const completedTime = new Date(task.completedDate).getTime()
        const inRange = completedTime >= start && completedTime <= end
        console.log('[TASK FILTER DEBUG] Completed task with completedDate:', {
          id: task.id,
          completedDate: task.completedDate,
          completedTime,
          inRange,
        })
        return inRange
      }

      // Fallback: legacy completed tasks without completedDate
      if (task.dueDate) {
        const dueTime = new Date(task.dueDate).getTime()
        const inRange = dueTime >= start && dueTime <= end
        console.log('[TASK FILTER DEBUG] Completed task fallback to dueDate:', {
          id: task.id,
          dueDate: task.dueDate,
          dueTime,
          inRange,
        })
        return inRange
      }

      // No date available - exclude
      console.log(
        '[TASK FILTER DEBUG] Completed task excluded (no dates):',
        task.id,
      )
      return false
    }

    // For incomplete tasks, use dueDate
    if (!task.dueDate) return false
    const taskDate = new Date(task.dueDate).getTime()
    return taskDate >= start && taskDate <= end
  })

  console.log('[TASK FILTER DEBUG] Filtered tasks count:', filtered.length)
  console.log(
    '[TASK FILTER DEBUG] Completed tasks in filtered result:',
    filtered.filter((t) => t.status === 'completed').length,
  )
  console.log('═══════════════════════════════════════════════════════')

  return filtered
}
